import React from 'react';
import { createRoot } from 'react-dom/client';
import * as Sentry from '@sentry/browser';
import { BrowserTracing } from '@sentry/browser';
import { HiveThemeProvider } from '@routific/hive';
import { Spinner, V5ThemeProvider } from '@routific/hive/v5';
import { Spin } from 'antd';
import { observer } from 'mobx-react';
import './i18n';
import './stylesheets/index.less';
// TODO: Figure out CSS modules loading correctly in dev build. 
// This should live at the component level but CSS imports with ESM work a little different and the overrides are not happening. 
import '@routific/react-calendar-timeline/lib/Timeline.css';
import AppRouter from './router';
import * as serviceWorker from './serviceWorker';
import ErrorPage from './components/errors/ErrorPage';
import { StoreProvider } from './store';
import { LoggingProvider } from './services/logglyLogger';
import { MSTProvider } from './store/mst';
import { useResolvedV5Theme, useV5DarkModeShortcut } from './hooks/useV5Theme';
import config from './config';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { registerVitePreloadErrorHandler } from './helpers/vitePreloadError';
import { registerDomTranslationGuard } from './helpers/domTranslationGuard';
import { initSegment } from './analytics/segment';

initSegment();

if (config.thirdParty.sentry.dsn) {
  Sentry.init({
    dsn: config.thirdParty.sentry.dsn,
    integrations: [
      new BrowserTracing({
        // The below is a white-list of backend targets that will get traced
        tracePropagationTargets: ['routific.com'],
      }),
    ],
    ignoreErrors: [
      'ResizeObserver loop limit exceeded',
      'ResizeObserver loop completed with undelivered notifications.',
    ],
    tracesSampleRate: config.thirdParty.sentry.traceSampleRate,
    environment: config.thirdParty.sentry.environment,
    release: config.thirdParty.sentry.release,
  });
}

registerVitePreloadErrorHandler();
registerDomTranslationGuard();

Spin.setDefaultIndicator(<Spinner size="sm" />);

const container = document.getElementById('root');

const ThemedApp = observer(() => {
  useV5DarkModeShortcut();
  const { colorMode, density } = useResolvedV5Theme();

  return (
    <V5ThemeProvider colorMode={colorMode} density={density}>
      <HiveThemeProvider>
        <StoreProvider>
          <LoggingProvider>
            <ErrorPage>
              <GoogleOAuthProvider clientId={config.thirdParty.google.ssoClientId}>
                <AppRouter />
              </GoogleOAuthProvider>
            </ErrorPage>
          </LoggingProvider>
        </StoreProvider>
      </HiveThemeProvider>
    </V5ThemeProvider>
  );
});

if (container) {
  const root = createRoot(container);
  root.render(
    <MSTProvider>
      <ThemedApp />
    </MSTProvider>,
  );
}

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA
serviceWorker.unregister();
